import React from "react";
import { Icon, Box, TooltipProps, Input, Textarea, Select, NumberInput, InputGroup } from "@chakra-ui/react";
import { InfoOutlineIcon } from "@chakra-ui/icons";
import { SafeTooltip as Tooltip } from "./SafeTooltip";

/**
 * AuthTip — the authentication kit's tooltip for NEW kit pieces (existing module code keeps `components/Tip.tsx`) (module-local, cart-in-module rule).
 *
 * Defaults fix the two orphaned-open tooltip bugs
 * (vault/how-to/how-to-build-new-module.md:1336 and the shifts InfoTip):
 *   - `closeDelay={0}` + `closeOnScroll` + `closeOnEsc` — nothing lingers once the
 *     pointer leaves, the page scrolls, or Escape is pressed.
 *   - `shouldWrapChildren` — a disabled button still shows WHY it is disabled, and a
 *     child that can't take a ref still gets a tooltip.
 * The second half of the fix lives in `../lib/blurClick.ts`: a tooltipped control keeps
 * focus after a click, and Chakra reopens the tooltip on focus — so handlers blur.
 *
 * Two modes: wrap (`<AuthTip label="…">{child}</AuthTip>`) or `asIcon` (an info icon
 * next to a label with nothing to wrap).
 *
 * Form fields fill their row (2026-09-12). Chakra's wrapper span is `inline-block`, so a full-width
 * Input inside it shrank to its natural ~210px — every dialog's fields were half-width. A Chakra
 * Input/Textarea/Select/NumberInput/InputGroup child is detected and wrapped in a full-width Box
 * instead; anything else that must fill its row (e.g. `AuthMarkdownEditor`) passes `block`. A field's
 * tooltip opens on mouse hover only, never on focus (it covered the label while typing).
 */
export interface AuthTipProps extends Omit<TooltipProps, "label" | "children"> {
  label: React.ReactNode;
  children?: React.ReactNode;
  asIcon?: boolean;
  iconSize?: number | string;
  /** Fill the row: wrap in a full-width Box, not Chakra's inline-block span. Automatic for Chakra fields. */
  block?: boolean;
}

const FIELD_TYPES = new Set<unknown>([Input, Textarea, Select, NumberInput, InputGroup]);

export const AuthTip: React.FC<AuthTipProps> = ({ label, children, asIcon, iconSize = 3, block, ...rest }) => {
  const props: Partial<TooltipProps> = {
    hasArrow: true,
    openDelay: 300,
    closeDelay: 0,
    placement: "top",
    whiteSpace: "pre-line",
    shouldWrapChildren: true,
    closeOnScroll: true,
    closeOnEsc: true,
    ...rest,
  };
  if (asIcon) {
    return (
      <Tooltip label={label} {...props}>
        <Box as="span" display="inline-flex" alignItems="center" ml={1} cursor="help" verticalAlign="middle">
          <Icon as={InfoOutlineIcon} boxSize={iconSize} color="gray.400" />
        </Box>
      </Tooltip>
    );
  }
  if (block || (React.isValidElement(children) && FIELD_TYPES.has(children.type))) {
    return (
      <Tooltip label={label} {...props} shouldWrapChildren={false}>
        <Box w="100%">
          {/* Hover only: focus would open it over the field's own label the moment a dialog autofocuses
              or a phone user taps in to type. The label and helper text carry what a reader needs. */}
          <Box onFocus={(e: React.FocusEvent) => e.stopPropagation()}>{children}</Box>
        </Box>
      </Tooltip>
    );
  }
  return (
    <Tooltip label={label} {...props}>
      {children}
    </Tooltip>
  );
};

export default AuthTip;
